import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, GripVertical, ImagePlus, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import Img from "@/components/Img";
import { http, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";

const move = (arr, from, to) => { const a = [...arr]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

export default function ImageManager({ images = [], onChange, testid = "image-manager", single = false }) {
  const t = useT();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const upload = async (files) => {
    setBusy(true);
    const urls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await http.post("/extranet/upload", fd);
        urls.push(data.url);
        toast.success(`${f.name}: ${data.original_kb} KB → ${data.sizes_kb.md} KB (mobile)`);
      } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    onChange(single ? urls.slice(-1) : [...images, ...urls]);
    setBusy(false);
    ref.current.value = "";
  };
  const drop = (i) => { if (drag !== null && drag !== i) onChange(move(images, drag, i)); setDrag(null); setOver(null); };
  const btn = "rounded bg-white/90 p-1 text-slate-800 hover:bg-white";

  return (
    <div data-testid={testid}>
      <div className="mt-1.5 grid grid-cols-3 gap-2 sm:grid-cols-5">
        {images.map((src, i) => (
          <div key={src} data-testid={`${testid}-item-${i}`} draggable={!single}
            onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { setDrag(null); setOver(null); }}
            onDragOver={(e) => { if (drag !== null) { e.preventDefault(); setOver(i); } }} onDrop={(e) => { e.preventDefault(); drop(i); }}
            className={`group relative aspect-[4/3] overflow-hidden rounded-md border transition-[opacity,box-shadow] ${single ? "" : "cursor-grab active:cursor-grabbing"} ${drag === i ? "opacity-40" : ""} ${over === i && drag !== i ? "border-[#006CE4] ring-2 ring-[#006CE4]" : "border-[#E7E0D6]"}`}>
            <Img src={src} size="sm" className="pointer-events-none h-full w-full object-cover" />
            {i === 0 && !single && <span className="absolute left-1 top-1 rounded bg-[#1C1917]/80 px-1.5 text-[10px] font-semibold text-white">Cover</span>}
            {!single && <span className="absolute right-1 top-1 rounded bg-white/85 p-0.5 text-slate-600"><GripVertical className="h-3.5 w-3.5" /></span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-gradient-to-t from-black/60 p-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
              <span className="flex gap-1">{!single && <>
                <button type="button" title={t("Geser ke kiri")} disabled={i === 0} data-testid={`${testid}-left-${i}`} onClick={() => onChange(move(images, i, i - 1))} className={`${btn} disabled:opacity-30`}><ChevronLeft className="h-3.5 w-3.5" /></button>
                <button type="button" title={t("Geser ke kanan")} disabled={i === images.length - 1} data-testid={`${testid}-right-${i}`} onClick={() => onChange(move(images, i, i + 1))} className={`${btn} disabled:opacity-30`}><ChevronRight className="h-3.5 w-3.5" /></button>
              </>}</span>
              <span className="flex gap-1">
                {i > 0 && !single && <button type="button" title={t("Jadikan sampul")} data-testid={`${testid}-cover-${i}`} onClick={() => onChange(move(images, i, 0))} className={btn}><Star className="h-3.5 w-3.5" /></button>}
                <button type="button" title={t("Hapus")} data-testid={`${testid}-remove-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))} className={`${btn} text-red-600`}><X className="h-3.5 w-3.5" /></button>
              </span>
            </div>
          </div>
        ))}
        <button type="button" data-testid={`${testid}-upload`} disabled={busy} onClick={() => ref.current.click()}
          className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-[#D6CCBE] text-xs font-semibold text-[#8A5A3B] transition-colors hover:bg-[#F7F2EA]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}{t("Unggah foto")}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple={!single} hidden data-testid={`${testid}-input`} onChange={(e) => upload([...e.target.files])} />
      <p className="mt-1 text-xs text-slate-500">{t("Foto otomatis dikompres (WebP) dalam 3 ukuran untuk web & mobile.")}
        {!single && images.length > 1 && <> {t("Geser foto untuk mengubah urutan; foto pertama jadi cover. Klik Simpan setelah selesai.")}</>}</p>
    </div>
  );
}
